import React, {
  useState, useCallback, useEffect, useId, useRef,
} from 'react'

import {
  classnames, Textarea, TextInput, Button, Icon, Chat,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, string, bool,
} from 'prop-types'

import UserAvatar from 'Pods/Shared/UserAvatar/UserAvatar'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { MAX_COMMENT_LENGTH } from '../../constants'

function Composer({
  placeholder,
  id = undefined,
  username = '',
  avatarUrl = null,
  isSubmitting = false,
  variant = 'comment',
  onSubmit,
}) {
  const t = useTranslation()
  const [value, setValue] = useState('')
  const isReply = variant === 'reply'
  const trimmed = value.trim()
  const canSubmit = trimmed.length > 0 && !isSubmitting
  const inputRef = useRef(null)
  // Triangle's Textarea/TextInput require an id, and it has to be distinct from
  // the `id` prop, which names the form for the Reply button's aria-controls.
  const inputId = useId()

  // The reply composer mounts on demand when its comment's Reply is clicked, so
  // move focus straight into it.
  useEffect(() => {
    if (isReply) {
      inputRef.current?.focus()
    }
  }, [isReply])

  const handleChange = useCallback((event) => {
    setValue(event.target.value)
  }, [])

  // The draft survives until the server accepts it: a rejected post (the comment
  // quota is only 2 per 5 minutes on one model) used to clear the box and leave
  // the user retyping from scratch. Post stays disabled meanwhile via
  // isSubmitting, so the wait cannot produce a double submit.
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault()

    if (trimmed.length === 0 || isSubmitting) {
      return
    }

    const accepted = await onSubmit(trimmed)

    if (accepted) {
      setValue('')
    }
  }, [trimmed, isSubmitting, onSubmit])

  return (
    <form
      id={id}
      className={classnames('comments-modal__composer', isReply && 'comments-modal__composer--reply')}
      onSubmit={handleSubmit}
    >
      {isReply ? (
        <>
          <UserAvatar img={avatarUrl} userName={username} size="small" />
          <TextInput
            isPilot
            id={inputId}
            size="small"
            innerRef={inputRef}
            wrapperClassName="comments-modal__composer-input"
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            maxLength={MAX_COMMENT_LENGTH}
            aria-label={placeholder}
          />
        </>
      ) : (
        // The username rides inside the bordered box (Figma); the char count and
        // Post button sit on the row below, outside the box.
        <div className="comments-modal__composer-box">
          <Textarea
            id={inputId}
            className="comments-modal__composer-input"
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            maxLength={MAX_COMMENT_LENGTH}
            aria-label={placeholder}
            rows={2}
            isNotResizable
          />
          <div className="comments-modal__composer-user">
            <Icon svgComponent={Chat} size="small" color="dark" />
            <span className="comments-modal__composer-username cgt-body-base-semibold">{username}</span>
          </div>
        </div>
      )}
      <div className="comments-modal__composer-footer">
        <div className="comments-modal__composer-actions">
          {!isReply && (
            <span className="comments-modal__char-count cgt-caption">
              {value.length}/{MAX_COMMENT_LENGTH}
            </span>
          )}
          <Button
            type="primary"
            size="small"
            actionType="submit"
            disabled={!canSubmit}
            customClass="comments-modal__post-button"
          >
            {t('commentsModal.post')}
          </Button>
        </div>
      </div>
    </form>
  )
}

Composer.propTypes = {
  placeholder: string.isRequired,
  onSubmit: func.isRequired,
  id: string,
  username: string,
  avatarUrl: string,
  isSubmitting: bool,
  variant: string,
}

export default Composer
